import React, { useMemo, useState } from 'react';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Avatar } from '@buildoutinc/blueprint-react/ui/Avatar';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Banner } from '@buildoutinc/blueprint-react/ui/Banner';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Dialog } from '@buildoutinc/blueprint-react/ui/Dialog';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';

import { Button } from 'components/ui/button';
import { Select } from 'components/ui/select';
import { showSnackbar } from 'components/ui/snackbar/helper';

import { put } from 'helpers/api';
import { requestErrors } from 'helpers/api_errors';
import { getInitials } from 'helpers/string_helpers';

import { dependencyDisabled } from 'components/users/permissions/permission_dependencies';

import OfficesField from './offices_field';
import OtherUsersCard, { oupModeOf } from './other_users_card';
import PermissionRowItem from './permission_row';
import RemovableBadge from './removable_badge';
import {
  attribution, blockedBy, dependencyNote, isCustom, labelsOf, roleDefaults, rowsOf, summarize, toSentence,
  valuesOf, withRoles, withValue,
} from './permission_state';

import type { OupMode } from 'bundles/company/permissions/oup_user_selector';
import type { PermissionValues } from './permission_state';
import type { RowState } from './permission_row';
import type { AssignableRole, PermissionRow, PermissionSummary, UserPermissionsViewProps, ViewRole } from './types';

const OFFICE_ADMIN = 'office_admin';
const FIELD_STYLE = { maxWidth: '22rem' };

// Everything an edit session can move. Captured when editing starts and restored on Cancel, because once a
// save has landed the read view renders this state directly — so an abandoned draft stayed on screen as
// though persisted, and the next edit resumed it.
interface Draft {
  values: PermissionValues;
  overridden: Set<string>;
  roleIds: number[];
  adminedOfficeIds: number[];
  oupUserIds: number[];
  oupOfficeIds: number[];
  oupMode: OupMode;
}

// "3 of 24 turned on · no custom changes"
const summaryText = ({ granted, total, customised }: PermissionSummary): string => {
  const changes = customised === 0
    ? 'no custom changes'
    : `${customised} custom change${customised === 1 ? '' : 's'}`;

  return `${granted} of ${total} turned on · ${changes}`;
};

export default function UserPermissionsView({
  user, groups, summary, explainer, canManage, availableRoles, dependencyRules, permissionLabels,
  graphOnlyPermissions, rolesEnableBackoffice, officeAdmin, oup, paths,
}: UserPermissionsViewProps) {
  const rows = useMemo(() => rowsOf(groups), [groups]);
  const labels = useMemo(() => labelsOf(permissionLabels), [permissionLabels]);

  const [editing, setEditing] = useState(false);
  // Once a save lands the rows describe state the browser owns, not what the serializer sent on page load.
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState<PermissionValues>(() => valuesOf(rows, graphOnlyPermissions));
  const [overridden, setOverridden] = useState<Set<string>>(
    () => new Set(rows.filter(row => row.overridden).map(row => row.key)),
  );
  const [roleIds, setRoleIds] = useState<number[]>(() => user.roles.map(role => role.id));
  const [adminedOfficeIds, setAdminedOfficeIds] = useState<number[]>(officeAdmin?.selectedOfficeIds ?? []);

  // Drop any stored OUP user IDs that aren't in the available users list (e.g. a deactivated user) so
  // they don't linger in state and get re-saved.
  const availableOupUserIds = (ids: number[]) => ids.filter(id => oup.users.some(oupUser => oupUser.id === id));
  const [oupUserIds, setOupUserIds] = useState<number[]>(() => availableOupUserIds(oup.selectedUserIds));
  // What the server holds, deactivated users included. An offices save sends no users, so these are what stay.
  const [storedOupUserIds, setStoredOupUserIds] = useState<number[]>(oup.selectedUserIds);
  const [oupOfficeIds, setOupOfficeIds] = useState<number[]>(oup.selectedOfficeIds);
  const [oupMode, setOupMode] = useState<OupMode>(oupModeOf(oup.selectedUserIds, oup.selectedOfficeIds));

  // can_have_properties is billing-affecting, so its grant is held here until it is confirmed.
  const [pendingConfirmKey, setPendingConfirmKey] = useState<string | null>(null);

  // What the state was when this edit session began. Cancel restores it.
  const [snapshot, setSnapshot] = useState<Draft | null>(null);

  // Recomputed from the selection rather than read off the row, which is what lets a role change re-decide
  // Default vs Custom without a round trip.
  const defaults = useMemo(
    () => roleDefaults(
      dependencyRules, rows, availableRoles, roleIds, values, snapshot, rolesEnableBackoffice, overridden,
    ),
    [dependencyRules, rows, availableRoles, roleIds, values, snapshot, rolesEnableBackoffice, overridden],
  );

  const selectedRoles = useMemo(
    () => availableRoles.filter(role => roleIds.includes(role.id)),
    [availableRoles, roleIds],
  );

  const unselectedRoles = useMemo(
    () => availableRoles.filter(role => !roleIds.includes(role.id)),
    [availableRoles, roleIds],
  );

  const officeOptions = officeAdmin?.officeOptions ?? [];

  // Not the same question as whether it is in `values`: office_admin appears in a locked_by rule, so the
  // serializer sends it among graphOnlyPermissions whether or not a row renders.
  const showsOfficeAdmin = useMemo(() => rows.some(row => row.key === OFFICE_ADMIN), [rows]);

  const showingOwnState = editing || saved;
  const liveSummary = showingOwnState ? summarize(rows, values, defaults, overridden) : summary;
  const headerRoles: ViewRole[] = showingOwnState ? selectedRoles : user.roles;

  // User validates that an office admin covers at least one office, so saving with none can only fail.
  // Only when the row is on screen: without it there is nothing to choose an office with.
  const officeAdminIncomplete = showsOfficeAdmin && values[OFFICE_ADMIN] && adminedOfficeIds.length === 0;

  const defaultsFor = (ids: number[], current: PermissionValues, held: Set<string>) => roleDefaults(
    dependencyRules, rows, availableRoles, ids, current, snapshot, rolesEnableBackoffice, held,
  );

  function changeRoles(nextRoleIds: number[]) {
    setValues(current => withRoles(
      dependencyRules, rows, current,
      defaultsFor(roleIds, current, overridden), defaultsFor(nextRoleIds, current, overridden), overridden,
    ));
    setRoleIds(nextRoleIds);
  }

  // Moving a toggle IS creating an override; one that agrees with the roles is dropped on save. Use Back Office
  // decides whether the roles' back office grants apply, so switching it re-applies them as a role change does.
  function applyChange(key: string, value: boolean) {
    setValues((current) => {
      const next = withValue(dependencyRules, current, key, value);
      if (!rows.some(row => row.key === key && row.featureSourced)) return next;

      const held = new Set(overridden).add(key);

      return withRoles(
        dependencyRules, rows, next, defaultsFor(roleIds, current, overridden), defaultsFor(roleIds, next, held), held,
      );
    });
    setOverridden(current => new Set(current).add(key));
  }

  function resetToRoleDefault(key: string) {
    setValues(current => withValue(dependencyRules, current, key, defaults[key]));
    setOverridden((current) => {
      const next = new Set(current);
      next.delete(key);

      return next;
    });
  }

  function handleChange(key: string, value: boolean, confirmMessage: string | null) {
    if (value && confirmMessage) {
      setPendingConfirmKey(key);
      return;
    }

    applyChange(key, value);
  }

  // The props as they arrived, which is what the page still describes until a save lands.
  function draftFromProps(): Draft {
    return {
      values: valuesOf(rows, graphOnlyPermissions),
      overridden: new Set(rows.filter(row => row.overridden).map(row => row.key)),
      roleIds: user.roles.map(role => role.id),
      adminedOfficeIds: officeAdmin?.selectedOfficeIds ?? [],
      oupUserIds: availableOupUserIds(oup.selectedUserIds),
      oupOfficeIds: oup.selectedOfficeIds,
      oupMode: oupModeOf(oup.selectedUserIds, oup.selectedOfficeIds),
    };
  }

  function applyDraft(draft: Draft) {
    setValues(draft.values);
    setOverridden(draft.overridden);
    setRoleIds(draft.roleIds);
    setAdminedOfficeIds(draft.adminedOfficeIds);
    setOupUserIds(draft.oupUserIds);
    setOupOfficeIds(draft.oupOfficeIds);
    setOupMode(draft.oupMode);
  }

  // Starts from whatever is on screen — the props before a save has landed, this page's own state after —
  // and keeps it, so Cancel has something to put back.
  function startEditing() {
    const draft = saved
      ? { values, overridden, roleIds, adminedOfficeIds, oupUserIds, oupOfficeIds, oupMode }
      : draftFromProps();

    applyDraft(draft);
    setSnapshot(draft);
    setEditing(true);
  }

  function cancelEditing() {
    if (snapshot) applyDraft(snapshot);

    setEditing(false);
  }

  // Row keys only: the graph-only permissions are carried so the dependency pass resolves over the same set
  // the server does, but submitting them would write permissions this page never showed anyone.
  function buildPayload() {
    const payload: Record<string, unknown> = { companyRoleIds: roleIds };

    rows.forEach((row) => {
      payload[row.key] = values[row.key];
    });

    // Keyed on the ROW, not on officeAdmin or `values`: the office props are built for any company with the
    // customization, the row also needs an elite back office admin viewing, and office_admin reaches `values`
    // through graphOnlyPermissions whether or not it renders. For a user resolving it true at a company that
    // has since dropped the customization, every save otherwise submitted an empty list and was 422'd
    // permanently.
    if (showsOfficeAdmin) payload.adminedOfficeIds = values[OFFICE_ADMIN] ? adminedOfficeIds : [];

    if (oupMode === 'offices') {
      payload.permissionsOtherOfficeIds = oupOfficeIds;
    } else {
      payload.permissionsOtherUserIds = oupMode === 'individual' ? oupUserIds : [];
      payload.clearPermissionsOtherOfficeIds = true;
    }

    return payload;
  }

  async function handleSave() {
    setSaving(true);

    try {
      await put(paths.update, { user: buildPayload() });
      // What the server now holds, so the read view describes what a reload would.
      const savedOfficeIds = oupMode === 'offices' ? oupOfficeIds : [];
      let savedUserIds = storedOupUserIds;
      if (oupMode !== 'offices') savedUserIds = oupMode === 'individual' ? oupUserIds : [];
      setStoredOupUserIds(savedUserIds);
      setOupOfficeIds(savedOfficeIds);
      setOupUserIds(availableOupUserIds(savedUserIds));
      setOupMode(oupModeOf(savedUserIds, savedOfficeIds));
      setSaved(true);
      setEditing(false);
      showSnackbar({ text: 'These permissions were saved.', variant: 'success' });
    } catch (error) {
      const text = requestErrors(error, 'Something went wrong saving these permissions.').join(' ');

      showSnackbar({ text, variant: 'danger' });
    } finally {
      setSaving(false);
    }
  }

  const officeSentence = () => toSentence(
    adminedOfficeIds.map(id => officeOptions.find(option => option.value === id)?.label).filter(Boolean) as string[],
  );

  function officeDetail(row: PermissionRow): React.ReactNode | null {
    if (row.key !== OFFICE_ADMIN || !values[OFFICE_ADMIN]) return null;
    if (!editing) return officeSentence();

    return (
      <div className="mt-2">
        <OfficesField
          ariaLabel="Offices this office admin covers"
          inputId="permissions-office-admin"
          options={officeOptions}
          selectedIds={adminedOfficeIds}
          onChange={setAdminedOfficeIds}
        />
      </div>
    );
  }

  function rowState(row: PermissionRow): RowState {
    const lockedByGraph = dependencyDisabled(dependencyRules, row.key, values, values[OFFICE_ADMIN] === true);

    return {
      granted: values[row.key],
      custom: isCustom(row, values, defaults, overridden),
      attribution: attribution(row, availableRoles, roleIds, values),
      blockedBy: blockedBy(dependencyRules, values, labels, row.key),
      note: row.lockReason ?? dependencyNote(dependencyRules, values, labels, row.key),
      detail: officeDetail(row),
      controls: editing
        ? {
          locked: row.locked || lockedByGraph,
          onChange: value => handleChange(row.key, value, row.confirmMessage),
          onReset: () => resetToRoleDefault(row.key),
        }
        : undefined,
    };
  }

  const confirmMessage = rows.find(row => row.key === pendingConfirmKey)?.confirmMessage ?? '';

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
        <div className="d-flex align-items-center gap-3">
          <Avatar>
            {user.photoUrl && <Avatar.Image alt="" src={user.photoUrl} />}
            <Avatar.Fallback>{getInitials(user.name)}</Avatar.Fallback>
          </Avatar>
          <div>
            <h5 className="mb-1">{user.name}</h5>
            {headerRoles.length === 0 ? (
              <span className="text-muted fs-small">No assigned roles</span>
            ) : (
              <div className="d-flex flex-wrap gap-1">
                {headerRoles.map(role => (editing ? (
                  <RemovableBadge
                    data-testid="assigned-role"
                    key={role.id}
                    label={role.name}
                    onRemove={() => changeRoles(roleIds.filter(id => id !== role.id))}
                  />
                ) : (
                  <Badge key={role.id} variant="outline">{role.name}</Badge>
                )))}
              </div>
            )}
          </div>
        </div>

        {canManage && !editing && (
          <Button data-testid="edit-permissions" variant="secondary" onClick={startEditing}>
            Edit Permissions
          </Button>
        )}
      </div>

      {editing && (
        <div className="mb-4" style={FIELD_STYLE}>
          <Label htmlFor="permissions-add-role">Add a role</Label>
          <Select<AssignableRole>
            aria-label="Add a role"
            getOptionLabel={role => role.name}
            getOptionValue={role => String(role.id)}
            inputId="permissions-add-role"
            options={unselectedRoles}
            placeholder="Select a role"
            value={null}
            onChange={role => role && changeRoles([...roleIds, role.id])}
          />
        </div>
      )}

      <p className="text-muted fs-small" data-testid="permissions-summary">{summaryText(liveSummary)}</p>

      {/* Suppressed once the selection has moved on from the one it describes. The serializer only sends
          it for a user with no roles, so it cannot be recomputed here — it reappears on the next load. */}
      {explainer && !editing && headerRoles.length === 0 && (
        <Banner className="mb-4" variant="info">
          <FontAwesomeIcon className="me-2" icon={faCircleInfo} />
          {explainer}
        </Banner>
      )}

      {groups.length === 0 ? (
        <Empty>
          <Empty.Title>No permissions apply to this account</Empty.Title>
        </Empty>
      ) : groups.map(group => (
        <Card className="mb-4" key={group.key}>
          <Card.Header>
            <Card.Title className="fw-semibold fs-large">{group.title}</Card.Title>
          </Card.Header>
          <Card.Body>
            <ul className="list-unstyled mb-0">
              {group.rows.map(row => (
                <PermissionRowItem key={row.key} row={row} state={showingOwnState ? rowState(row) : undefined} />
              ))}
            </ul>
          </Card.Body>
        </Card>
      ))}

      {groups.length > 0 && (
        <OtherUsersCard
          editing={editing}
          mode={oupMode}
          oup={oup}
          selectedOfficeIds={oupOfficeIds}
          selectedUserIds={oupUserIds}
          onChangeMode={setOupMode}
          onChangeOfficeIds={setOupOfficeIds}
          onChangeUserIds={setOupUserIds}
        />
      )}

      {editing && (
        <div className="d-flex gap-2">
          <Button
            data-testid="save-permissions"
            disabled={saving || officeAdminIncomplete}
            disabledTooltip={officeAdminIncomplete ? 'Choose at least one office for the office admin.' : undefined}
            onClick={handleSave}
          >
            Save Permissions
          </Button>
          <Button
            data-testid="cancel-permissions"
            disabled={saving}
            variant="outline"
            onClick={cancelEditing}
          >
            Cancel
          </Button>
        </div>
      )}

      <Dialog open={pendingConfirmKey !== null} onClose={() => setPendingConfirmKey(null)}>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Confirm Permission Change</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <p data-testid="confirm-message">{confirmMessage}</p>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Cancel>Cancel</Dialog.Cancel>
            <Button
              data-testid="confirm-permission"
              onClick={() => {
                if (pendingConfirmKey) applyChange(pendingConfirmKey, true);
                setPendingConfirmKey(null);
              }}
            >
              Confirm
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </div>
  );
}
